/**
 * Tenant Settings Tab
 * Manage tenant branding, features, and security settings
 */

import { useState } from "react";
import {
  useTenantSettings,
  useUpdateTenantSettings,
} from "@hooks/useTenantAdmin";
import type {
  TenantBrandingSettings,
  TenantFeatureSettings,
  TenantSecuritySettings,
} from "@/types/tenantAdmin";
import { cn } from "@utils/cn";

// Section wrapper component
interface SettingsSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function SettingsSection({
  title,
  description,
  children,
}: SettingsSectionProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      </div>
      {children}
    </div>
  );
}

// Toggle switch component
interface ToggleSwitchProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
  disabled,
}: ToggleSwitchProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white">
          {label}
        </label>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={cn(
          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
          checked ? "bg-primary-600" : "bg-gray-200 dark:bg-gray-700",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <span
          className={cn(
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
            checked ? "translate-x-5" : "translate-x-0",
          )}
        />
      </button>
    </div>
  );
}

// Text input component
interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "url" | "email" | "number";
  disabled?: boolean;
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
}: TextInputProps) {
  return (
    <div className="py-3">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg",
          "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
          "focus:ring-2 focus:ring-primary-500 focus:border-transparent",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      />
    </div>
  );
}

// Color picker component
interface ColorPickerProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

function ColorPicker({ label, value, onChange, disabled }: ColorPickerProps) {
  return (
    <div className="py-3">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || "#4F46E5"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={cn(
            "w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="#4F46E5"
          disabled={disabled}
          className={cn(
            "flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg",
            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
            "focus:ring-2 focus:ring-primary-500 focus:border-transparent",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

// Number input with optional max
interface NumberInputProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  description?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
}

function NumberInput({
  label,
  value,
  onChange,
  description,
  min,
  max,
  disabled,
}: NumberInputProps) {
  return (
    <div className="py-3">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
        {label}
      </label>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          {description}
        </p>
      )}
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value ? parseInt(e.target.value) : null)
          }
          min={min}
          max={max}
          disabled={disabled}
          placeholder="Unlimited"
          className={cn(
            "w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg",
            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
            "focus:ring-2 focus:ring-primary-500 focus:border-transparent",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {value === null ? "(Unlimited)" : ""}
        </span>
      </div>
    </div>
  );
}

// Email domains input
interface EmailDomainsInputProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  description?: string;
  disabled?: boolean;
}

function EmailDomainsInput({
  label,
  value,
  onChange,
  description,
  disabled,
}: EmailDomainsInputProps) {
  const [inputValue, setInputValue] = useState("");

  const addDomain = () => {
    const domain = inputValue.trim().toLowerCase();
    if (domain && !value.includes(domain)) {
      onChange([...value, domain]);
      setInputValue("");
    }
  };

  const removeDomain = (domain: string) => {
    onChange(value.filter((d) => d !== domain));
  };

  return (
    <div className="py-3">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
        {label}
      </label>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          {description}
        </p>
      )}
      <div className="flex items-center gap-2 mb-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && (e.preventDefault(), addDomain())
          }
          placeholder="example.com"
          disabled={disabled}
          className={cn(
            "flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg",
            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
            "focus:ring-2 focus:ring-primary-500 focus:border-transparent",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
        <button
          type="button"
          onClick={addDomain}
          disabled={disabled || !inputValue.trim()}
          className={cn(
            "px-4 py-2 bg-primary-600 text-white rounded-lg",
            "hover:bg-primary-700 transition-colors",
            (disabled || !inputValue.trim()) && "opacity-50 cursor-not-allowed",
          )}
        >
          Add
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((domain) => (
            <span
              key={domain}
              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm"
            >
              {domain}
              <button
                type="button"
                onClick={() => removeDomain(domain)}
                disabled={disabled}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      {value.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No domains specified (any email domain allowed)
        </p>
      )}
    </div>
  );
}

export function TenantSettingsTab() {
  const { data: settingsData, isLoading, error } = useTenantSettings();
  const updateSettings = useUpdateTenantSettings();

  // Local state for form values
  const [branding, setBranding] = useState<Partial<TenantBrandingSettings>>({});
  const [features, setFeatures] = useState<Partial<TenantFeatureSettings>>({});
  const [security, setSecurity] = useState<Partial<TenantSecuritySettings>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Get current values (merged with updates)
  const currentBranding = {
    ...settingsData?.settings.branding,
    ...branding,
  };
  const currentFeatures = {
    ...settingsData?.settings.features,
    ...features,
  };
  const currentSecurity = {
    ...settingsData?.settings.security,
    ...security,
  };

  // Update handlers
  const updateBranding = (
    key: keyof TenantBrandingSettings,
    value: string | null,
  ) => {
    setBranding((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateFeatures = (
    key: keyof TenantFeatureSettings,
    value: boolean | number | null,
  ) => {
    setFeatures((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateSecurity = (
    key: keyof TenantSecuritySettings,
    value: boolean | string[] | number,
  ) => {
    setSecurity((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Save handler
  const handleSave = async () => {
    await updateSettings.mutateAsync({
      branding: Object.keys(branding).length > 0 ? branding : undefined,
      features: Object.keys(features).length > 0 ? features : undefined,
      security: Object.keys(security).length > 0 ? security : undefined,
    });
    setBranding({});
    setFeatures({});
    setSecurity({});
    setHasChanges(false);
  };

  // Reset handler
  const handleReset = () => {
    setBranding({});
    setFeatures({});
    setSecurity({});
    setHasChanges(false);
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
          Failed to load settings
        </h3>
        <p className="mt-2 text-red-600 dark:text-red-300">
          Unable to fetch organization settings. Please try again later.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
            <div className="space-y-4">
              {[1, 2, 3].map((j) => (
                <div
                  key={j}
                  className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with save/reset buttons */}
      {hasChanges && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-primary-700 dark:text-primary-300">
            You have unsaved changes
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={updateSettings.isPending}
              className={cn(
                "px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg",
                "hover:bg-primary-700 transition-colors",
                updateSettings.isPending && "opacity-50 cursor-not-allowed",
              )}
            >
              {updateSettings.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Branding Settings */}
      <SettingsSection
        title="Branding"
        description="Customize your organization's appearance"
      >
        <TextInput
          label="Logo URL"
          value={currentBranding.logoUrl || ""}
          onChange={(v) => updateBranding("logoUrl", v || null)}
          placeholder="https://example.com/logo.png"
          type="url"
        />
        <ColorPicker
          label="Primary Color"
          value={currentBranding.primaryColor || null}
          onChange={(v) => updateBranding("primaryColor", v)}
        />
        <ColorPicker
          label="Secondary Color"
          value={currentBranding.secondaryColor || null}
          onChange={(v) => updateBranding("secondaryColor", v)}
        />
      </SettingsSection>

      {/* Feature Settings */}
      <SettingsSection
        title="Features"
        description="Enable or disable platform features for your organization"
      >
        <ToggleSwitch
          label="Agent Builder"
          description="Allow users to create agents using the Visual Agent Builder"
          checked={currentFeatures.enableAgentBuilder ?? true}
          onChange={(v) => updateFeatures("enableAgentBuilder", v)}
        />
        <ToggleSwitch
          label="Template Library"
          description="Allow users to browse and use agent templates"
          checked={currentFeatures.enableTemplates ?? true}
          onChange={(v) => updateFeatures("enableTemplates", v)}
        />
        <ToggleSwitch
          label="Learning Library"
          description="Allow users to access learning resources"
          checked={currentFeatures.enableLibrary ?? true}
          onChange={(v) => updateFeatures("enableLibrary", v)}
        />
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <NumberInput
            label="Maximum Workshops"
            value={currentFeatures.maxWorkshops ?? null}
            onChange={(v) => updateFeatures("maxWorkshops", v)}
            description="Leave empty for unlimited workshops"
            min={0}
          />
          <NumberInput
            label="Maximum Users"
            value={currentFeatures.maxUsers ?? null}
            onChange={(v) => updateFeatures("maxUsers", v)}
            description="Leave empty for unlimited users"
            min={0}
          />
        </div>
      </SettingsSection>

      {/* Security Settings */}
      <SettingsSection
        title="Security"
        description="Configure security policies for your organization"
      >
        <ToggleSwitch
          label="Require MFA"
          description="Require multi-factor authentication for all users"
          checked={currentSecurity.requireMfa ?? false}
          onChange={(v) => updateSecurity("requireMfa", v)}
        />
        <NumberInput
          label="Session Timeout (minutes)"
          value={currentSecurity.sessionTimeoutMinutes ?? 60}
          onChange={(v) => updateSecurity("sessionTimeoutMinutes", v ?? 60)}
          description="How long before inactive sessions expire"
          min={5}
          max={1440}
        />
        <EmailDomainsInput
          label="Allowed Email Domains"
          value={currentSecurity.allowedEmailDomains ?? []}
          onChange={(v) => updateSecurity("allowedEmailDomains", v)}
          description="Only allow users with email addresses from these domains to sign up"
        />
      </SettingsSection>
    </div>
  );
}
